'use client';

import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

import Icon from '@/components/ui/AppIcon';

const PrivacyPolicyInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-secondary">
        <div className="mx-auto max-w-content px-6 lg:px-8 py-16 lg:py-20">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-md w-1/3"></div>
            <div className="space-y-4">
              <div className="h-6 bg-muted rounded"></div>
              <div className="h-6 bg-muted rounded"></div>
              <div className="h-6 bg-muted rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-secondary relative overflow-hidden">
      <div className="mx-auto max-w-content px-6 lg:px-8 py-16 lg:py-20">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="font-heading text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            {t('privacy.title')}
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('privacy.subtitle')}
          </p>
          <div className="w-24 h-1 bg-gradient-gold mx-auto mt-8 rounded-full" />
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-luxury-md p-10 lg:p-12 border border-border/50 space-y-10">
            {/* Introduction */}
            <section>
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="ShieldCheckIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.intro.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.intro.content1')}</p>
                <p>{t('privacy.intro.content2')}</p>
              </div>
            </section>

            {/* Data Controller */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="BuildingOfficeIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.controller.title')}
              </h2>
              <div className="space-y-3 font-body text-base text-foreground leading-relaxed">
                <p className="font-semibold text-lg">G. Sas-Suppert Einzelunternehmen – GOLD DREAMS</p>
                <p>Peterskopfstr. 2</p>
                <p>67269 Grünstadt, Deutschland</p>
                <p className="mt-4"><span className="font-semibold">{t('privacy.controller.email')}:</span> info@golddreams.eu</p>
                <p><span className="font-semibold">{t('privacy.controller.phone')}:</span> +49 (0) 6359 86762</p>
                <p><span className="font-semibold">{t('privacy.controller.phone')}:</span> +49 (0) 172 297 30 22</p>
              </div>
            </section>

            {/* Data Collection */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="DocumentTextIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.collection.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.collection.intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacy.collection.item1')}</li>
                  <li>{t('privacy.collection.item2')}</li>
                  <li>{t('privacy.collection.item3')}</li>
                  <li>{t('privacy.collection.item4')}</li>
                  <li>{t('privacy.collection.item5')}</li>
                </ul>
              </div>
            </section>

            {/* Purpose of Processing */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="CogIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.purpose.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.purpose.intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacy.purpose.item1')}</li>
                  <li>{t('privacy.purpose.item2')}</li>
                  <li>{t('privacy.purpose.item3')}</li>
                  <li>{t('privacy.purpose.item4')}</li>
                  <li>{t('privacy.purpose.item5')}</li>
                </ul>
              </div>
            </section>

            {/* Legal Basis */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="ScaleIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.legal.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.legal.content1')}</p>
                <p>{t('privacy.legal.content2')}</p>
                <p>{t('privacy.legal.content3')}</p>
              </div>
            </section>

            {/* Data Retention */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="ClockIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.retention.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.retention.content1')}</p>
                <p>{t('privacy.retention.content2')}</p>
              </div>
            </section>

            {/* Your Rights */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="UserIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.rights.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.rights.intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><span className="font-semibold">{t('privacy.rights.access')}:</span> {t('privacy.rights.accessDesc')}</li>
                  <li><span className="font-semibold">{t('privacy.rights.rectification')}:</span> {t('privacy.rights.rectificationDesc')}</li>
                  <li><span className="font-semibold">{t('privacy.rights.erasure')}:</span> {t('privacy.rights.erasureDesc')}</li>
                  <li><span className="font-semibold">{t('privacy.rights.restriction')}:</span> {t('privacy.rights.restrictionDesc')}</li>
                  <li><span className="font-semibold">{t('privacy.rights.portability')}:</span> {t('privacy.rights.portabilityDesc')}</li>
                  <li><span className="font-semibold">{t('privacy.rights.objection')}:</span> {t('privacy.rights.objectionDesc')}</li>
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="LockClosedIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.security.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.security.content1')}</p>
                <p>{t('privacy.security.content2')}</p>
              </div>
            </section>

            {/* Cookies */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="GlobeAltIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.cookies.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.cookies.content1')}</p>
                <p>{t('privacy.cookies.content2')}</p>
              </div>
            </section>

            {/* Contact */}
            <section className="border-t border-border/50 pt-10">
              <h2 className="font-heading text-3xl font-bold text-primary mb-6 tracking-tight flex items-center gap-3">
                <Icon name="EnvelopeIcon" size={28} className="text-accent" variant="outline" />
                {t('privacy.contact.title')}
              </h2>
              <div className="space-y-4 font-body text-base text-foreground leading-relaxed">
                <p>{t('privacy.contact.content')}</p>
                <p className="font-semibold">Email: info@golddreams.eu</p>
              </div>
            </section>

            {/* Last Updated */}
            <section className="border-t border-border/50 pt-10">
              <p className="font-body text-sm text-muted-foreground italic">
                {t('privacy.lastUpdated')}: {new Date()?.toLocaleDateString()}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>);

};

export default PrivacyPolicyInteractive;